import { useCallback, useEffect, useRef, useState } from 'react';
import type { Attachment } from './ChatPanel';

/**
 * Snapshot & notes tool (spec 35g) — a light-mode sketch surface over a picture of the running preview.
 *
 * Left rail = tools (Add note, Pen w/ a vertical size range, Eraser, Circle, Rectangle, Arrow). Right rail
 * = 5 preset colors + a custom picker. Notes are pins that open into sleek comment THREADS (anyone can
 * add more; empty notes are discarded on close). "Send notes" flattens the drawing + numbered pins into
 * one PNG and ships it plus every note's thread to the AI collaborator as a single bulk edit.
 *
 * All geometry is stored RELATIVE (0..1) so it survives the snapshot being scaled/zoomed; stroke widths
 * are on-screen px (non-scaling-stroke) and scaled to natural resolution on export. Keyboard: Esc close,
 * Ctrl/Cmd+Z undo, Ctrl/Cmd+Shift+Z or Ctrl+Y redo. Hold Shift/Ctrl while dragging a shape for a perfect
 * square/circle.
 */

interface Pt {
  x: number;
  y: number;
}
interface Comment {
  id: string;
  author: string;
  text: string;
}
interface Note {
  id: string;
  x: number;
  y: number;
  open: boolean;
  comments: Comment[];
}
interface PenItem {
  id: string;
  kind: 'pen';
  points: Pt[];
  color: string;
  size: number;
}
interface ShapeItem {
  id: string;
  kind: 'rect' | 'circle' | 'arrow';
  a: Pt;
  b: Pt;
  color: string;
  size: number;
}
type Item = PenItem | ShapeItem;
type Tool = 'note' | 'pen' | 'eraser' | 'circle' | 'rect' | 'arrow';

/** five main colors only — no shades; a custom picker covers everything else */
const COLORS = ['#ef4444', '#f59e0b', '#22c55e', '#3b82f6', '#111827'];
const SIZES = [2, 4, 6, 9, 14];
const ERASE_R = 0.022;
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 3;
const ZOOM_STEP = 0.25;

interface SnapshotAnnotatorProps {
  src: string;
  busy: boolean;
  authorName: string;
  onClose: () => void;
  onSend: (prompt: string, attachments: Attachment[], comments: string[]) => Promise<void>;
}

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));
const bbox = (a: Pt, b: Pt) => ({ x: Math.min(a.x, b.x), y: Math.min(a.y, b.y), w: Math.abs(a.x - b.x), h: Math.abs(a.y - b.y) });
const dist = (a: Pt, b: Pt) => Math.hypot(a.x - b.x, a.y - b.y);
const initials = (s: string) => (s.replace(/[^a-zA-Z0-9]/g, '').slice(0, 2).toUpperCase() || '?');

function describePos(x: number, y: number): string {
  const col = x < 0.34 ? 'left' : x > 0.66 ? 'right' : 'center';
  const row = y < 0.34 ? 'top' : y > 0.66 ? 'bottom' : 'middle';
  return `${row}-${col}`;
}

/** the two arrowhead barbs (relative coords), computed in on-screen space via the image aspect ratio */
function arrowBarbs(a: Pt, b: Pt, aspect: number) {
  const sx = aspect || 1;
  const Ax = a.x * sx,
    Ay = a.y,
    Bx = b.x * sx,
    By = b.y;
  const L = Math.hypot(Bx - Ax, By - Ay) || 1e-6;
  const ang = Math.atan2(By - Ay, Bx - Ax);
  const head = Math.min(0.11 * L, 0.025);
  const spread = 0.42;
  return {
    p1: { x: (Bx - head * Math.cos(ang - spread)) / sx, y: By - head * Math.sin(ang - spread) },
    p2: { x: (Bx - head * Math.cos(ang + spread)) / sx, y: By - head * Math.sin(ang + spread) },
  };
}

/* ---------------------------------- icons (stroke SVG) ---------------------------------- */
const IconNote = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 11.5a8.4 8.4 0 0 1-11.7 7.7L4 21l1.8-5.3A8.4 8.4 0 1 1 21 11.5z" />
  </svg>
);
const IconPen = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
  </svg>
);
const IconEraser = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 15.5 12 7.5a2 2 0 0 1 2.8 0l4 4a2 2 0 0 1 0 2.8L14 20H8l-4-4a1 1 0 0 1 0-.5z" />
    <path d="M9 20h11" />
  </svg>
);
const IconCircle = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <circle cx="12" cy="12" r="8.5" />
  </svg>
);
const IconRect = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <rect x="4" y="5.5" width="16" height="13" rx="1.5" />
  </svg>
);
const IconArrow = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 19 19 5M10 5h9v9" />
  </svg>
);
const IconUndo = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 7 4 12l5 5" />
    <path d="M4 12h11a5 5 0 0 1 0 10h-1" />
  </svg>
);
const IconRedo = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <path d="m15 7 5 5-5 5" />
    <path d="M20 12H9a5 5 0 0 0 0 10h1" />
  </svg>
);
const IconClose = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);
const IconTrash = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13" />
  </svg>
);
const IconSend = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 19V5M6 11l6-6 6 6" />
  </svg>
);
const IconZoomIn = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4.3-4.3M11 8v6M8 11h6" />
  </svg>
);
const IconZoomOut = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4.3-4.3M8 11h6" />
  </svg>
);

export function SnapshotAnnotator({ src, busy, authorName, onClose, onSend }: SnapshotAnnotatorProps) {
  const [tool, setTool] = useState<Tool>('note');
  const [notes, setNotes] = useState<Note[]>([]);
  const [drawings, setDrawings] = useState<Item[]>([]);
  const [redoStack, setRedoStack] = useState<Item[]>([]);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [color, setColor] = useState(COLORS[0]); // red by default
  const [size, setSize] = useState(SIZES[1]);
  const [zoom, setZoom] = useState(1);
  const [aspect, setAspect] = useState(1);
  const [sending, setSending] = useState(false);
  const surfaceRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const drawRef = useRef<Item | null>(null);
  const erasingRef = useRef(false);
  const dragRef = useRef<{ id: string; moved: boolean } | null>(null);
  const [, force] = useState(0);

  const showSizes = tool === 'pen' || tool === 'circle' || tool === 'rect' || tool === 'arrow';

  const rel = useCallback((clientX: number, clientY: number): Pt => {
    const r = surfaceRef.current?.getBoundingClientRect();
    if (!r || r.width === 0) return { x: 0, y: 0 };
    return { x: clamp01((clientX - r.left) / r.width), y: clamp01((clientY - r.top) / r.height) };
  }, []);

  const capture = (e: React.PointerEvent) => {
    try {
      surfaceRef.current?.setPointerCapture(e.pointerId);
    } catch {
      /* best-effort */
    }
  };

  const eraseAt = (p: Pt) =>
    setDrawings(ds =>
      ds.filter(d => {
        if (d.kind === 'pen') return !d.points.some(pt => dist(pt, p) <= ERASE_R);
        const bb = bbox(d.a, d.b);
        return !(p.x >= bb.x - ERASE_R && p.x <= bb.x + bb.w + ERASE_R && p.y >= bb.y - ERASE_R && p.y <= bb.y + bb.h + ERASE_R);
      }),
    );

  /* ------------------------------- surface pointer handlers ------------------------------- */
  const onPointerDown = (e: React.PointerEvent) => {
    if (busy || sending) return;
    if ((e.target as HTMLElement).closest('[data-ui]')) return;
    const p = rel(e.clientX, e.clientY);
    if (tool === 'note') {
      const id = `n-${Date.now().toString(36)}`;
      // opening a new note discards any half-started (empty) note and collapses the rest
      setNotes(ns => [...ns.flatMap(n => (n.comments.length === 0 ? [] : [{ ...n, open: false }])), { id, x: p.x, y: p.y, open: true, comments: [] }]);
      return;
    }
    if (tool === 'eraser') {
      erasingRef.current = true;
      eraseAt(p);
      capture(e);
      return;
    }
    if (tool === 'pen') drawRef.current = { id: `d-${Date.now().toString(36)}`, kind: 'pen', points: [p], color, size };
    else drawRef.current = { id: `d-${Date.now().toString(36)}`, kind: tool, a: p, b: p, color, size };
    capture(e);
    force(v => v + 1);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const p0 = rel(e.clientX, e.clientY);
    if (erasingRef.current) {
      eraseAt(p0);
      return;
    }
    const d = drawRef.current;
    if (!d) return;
    if (d.kind === 'pen') d.points.push(p0);
    else {
      let p = p0;
      // Shift / Ctrl → constrain a rect/circle to equal ON-SCREEN width & height (square / circle)
      if ((e.shiftKey || e.ctrlKey) && (d.kind === 'rect' || d.kind === 'circle')) {
        const sx = aspect || 1;
        const sdx = (p.x - d.a.x) * sx;
        const sdy = p.y - d.a.y;
        const m = Math.max(Math.abs(sdx), Math.abs(sdy));
        p = { x: clamp01(d.a.x + (Math.sign(sdx) || 1) * (m / sx)), y: clamp01(d.a.y + (Math.sign(sdy) || 1) * m) };
      }
      d.b = p;
    }
    force(v => v + 1);
  };
  const onPointerUp = () => {
    if (erasingRef.current) {
      erasingRef.current = false;
      return;
    }
    const d = drawRef.current;
    drawRef.current = null;
    if (d) {
      const keep = d.kind === 'pen' ? d.points.length > 1 : dist(d.a, d.b) > 0.01;
      if (keep) {
        setDrawings(ds => [...ds, d]);
        setRedoStack([]); // a fresh mark invalidates the redo trail
      }
    }
    force(v => v + 1);
  };

  /* ---------------------------------- pin drag / open ---------------------------------- */
  const onPinDown = (e: React.PointerEvent, id: string) => {
    e.stopPropagation();
    dragRef.current = { id, moved: false };
    try {
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    } catch {
      /* best-effort */
    }
  };
  const onPinMove = (e: React.PointerEvent, id: string) => {
    if (!dragRef.current || dragRef.current.id !== id) return;
    e.stopPropagation();
    dragRef.current.moved = true;
    const p = rel(e.clientX, e.clientY);
    setNotes(ns => ns.map(m => (m.id === id ? { ...m, x: p.x, y: p.y } : m)));
  };
  const onPinUp = (e: React.PointerEvent, id: string) => {
    e.stopPropagation();
    const d = dragRef.current;
    dragRef.current = null;
    if (d && !d.moved) {
      // tap toggles this note; collapsing an empty note discards it, and so do the others
      setNotes(ns =>
        ns.flatMap(m => {
          if (m.id !== id) return m.comments.length === 0 ? [] : [{ ...m, open: false }];
          const willOpen = !m.open;
          if (!willOpen && m.comments.length === 0) return [];
          return [{ ...m, open: willOpen }];
        }),
      );
    }
  };

  /* ------------------------------------ note thread ------------------------------------ */
  const addComment = (id: string) => {
    const text = (drafts[id] || '').trim();
    if (!text) return;
    setNotes(ns => ns.map(m => (m.id === id ? { ...m, comments: [...m.comments, { id: `c-${Date.now().toString(36)}`, author: authorName, text }] } : m)));
    setDrafts(d => ({ ...d, [id]: '' }));
  };
  const removeComment = (noteId: string, commentId: string) =>
    setNotes(ns => ns.map(m => (m.id === noteId ? { ...m, comments: m.comments.filter(c => c.id !== commentId) } : m)));
  const deleteNote = (id: string) => setNotes(ns => ns.filter(m => m.id !== id));
  const closeNote = (id: string) => setNotes(ns => ns.flatMap(n => (n.id !== id ? [n] : n.comments.length === 0 ? [] : [{ ...n, open: false }])));

  /* -------------------------------------- history -------------------------------------- */
  const undo = useCallback(() => {
    if (drawings.length) {
      const last = drawings[drawings.length - 1];
      setDrawings(d => d.slice(0, -1));
      setRedoStack(r => [...r, last]);
    } else if (notes.length) {
      setNotes(n => n.slice(0, -1));
    }
  }, [drawings, notes]);
  const redo = useCallback(() => {
    if (!redoStack.length) return;
    const item = redoStack[redoStack.length - 1];
    setRedoStack(r => r.slice(0, -1));
    setDrawings(d => [...d, item]);
  }, [redoStack]);

  const clearAll = () => {
    setDrawings([]);
    setNotes([]);
    setDrafts({});
    setRedoStack([]);
    drawRef.current = null;
  };
  const zoomBy = (delta: number) => setZoom(z => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, +(z + delta).toFixed(2))));

  /* --------------------------- flatten to one PNG for the model --------------------------- */
  const flatten = useCallback(
    (): Promise<string | null> =>
      new Promise(resolve => {
        const img = new Image();
        img.onload = () => {
          const c = document.createElement('canvas');
          c.width = img.naturalWidth || 1000;
          c.height = img.naturalHeight || 700;
          const ctx = c.getContext('2d');
          if (!ctx) return resolve(null);
          ctx.drawImage(img, 0, 0, c.width, c.height);
          const scale = c.width / (imgRef.current?.clientWidth || c.width);
          ctx.lineJoin = 'round';
          ctx.lineCap = 'round';
          for (const d of drawings) {
            ctx.strokeStyle = d.color;
            ctx.lineWidth = Math.max(1, d.size * scale);
            ctx.beginPath();
            if (d.kind === 'pen') {
              d.points.forEach((pt, i) => (i === 0 ? ctx.moveTo(pt.x * c.width, pt.y * c.height) : ctx.lineTo(pt.x * c.width, pt.y * c.height)));
            } else if (d.kind === 'rect') {
              const bb = bbox(d.a, d.b);
              ctx.rect(bb.x * c.width, bb.y * c.height, bb.w * c.width, bb.h * c.height);
            } else if (d.kind === 'circle') {
              const bb = bbox(d.a, d.b);
              ctx.ellipse((bb.x + bb.w / 2) * c.width, (bb.y + bb.h / 2) * c.height, (bb.w / 2) * c.width, (bb.h / 2) * c.height, 0, 0, Math.PI * 2);
            } else {
              const A = { x: d.a.x * c.width, y: d.a.y * c.height };
              const B = { x: d.b.x * c.width, y: d.b.y * c.height };
              const L = Math.hypot(B.x - A.x, B.y - A.y) || 1;
              const ang = Math.atan2(B.y - A.y, B.x - A.x);
              const head = Math.min(0.11 * L, Math.max(4, 0.011 * c.width));
              const spread = 0.42;
              ctx.moveTo(A.x, A.y);
              ctx.lineTo(B.x, B.y);
              ctx.moveTo(B.x - head * Math.cos(ang - spread), B.y - head * Math.sin(ang - spread));
              ctx.lineTo(B.x, B.y);
              ctx.lineTo(B.x - head * Math.cos(ang + spread), B.y - head * Math.sin(ang + spread));
            }
            ctx.stroke();
          }
          const pins = notes.filter(n => n.comments.length > 0);
          const r = Math.max(11, c.width / 55);
          ctx.font = `bold ${Math.round(r * 1.1)}px system-ui, sans-serif`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          pins.forEach((n, i) => {
            const X = n.x * c.width;
            const Y = n.y * c.height;
            ctx.beginPath();
            ctx.arc(X, Y, r, 0, Math.PI * 2);
            ctx.fillStyle = '#2b62b8'; // brand accent — must match the note chips in the lightbox
            ctx.fill();
            ctx.lineWidth = Math.max(2, r / 5);
            ctx.strokeStyle = '#fff';
            ctx.stroke();
            ctx.fillStyle = '#fff';
            ctx.fillText(String(i + 1), X, Y + 1);
          });
          try {
            resolve(c.toDataURL('image/png'));
          } catch {
            resolve(null);
          }
        };
        img.onerror = () => resolve(null);
        img.src = src;
      }),
    [src, drawings, notes],
  );

  /**
   * A close-up of what one pin actually points at, from the CLEAN screenshot (no doodles — they
   * would cover the very pixels the model needs). This is the grounding fix: with only the full
   * frame plus the words "top-center", the model resolved the target from the TEXT and restyled the
   * topmost element of a different screen. A tight crop leaves it nothing to misread.
   */
  const cropAt = (x: number, y: number): Promise<string | null> =>
    new Promise(resolve => {
      const img = new Image();
      img.onload = () => {
        try {
          const side = Math.round(Math.min(img.naturalWidth, img.naturalHeight) * 0.38);
          const sx = Math.max(0, Math.min(img.naturalWidth - side, Math.round(x * img.naturalWidth - side / 2)));
          const sy = Math.max(0, Math.min(img.naturalHeight - side, Math.round(y * img.naturalHeight - side / 2)));
          const c = document.createElement('canvas');
          const out = Math.min(512, side);
          c.width = out;
          c.height = out;
          const ctx = c.getContext('2d');
          if (!ctx) return resolve(null);
          ctx.drawImage(img, sx, sy, side, side, 0, 0, out, out);
          resolve(c.toDataURL('image/png'));
        } catch {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = src;
    });

  const send = async () => {
    const filled = notes.filter(n => n.comments.length > 0);
    if (filled.length === 0 && drawings.length === 0) return;
    setSending(true);
    try {
      const png = await flatten();
      /*
       * TWO formats on purpose. The display lines (notes panel, thread metrics) keep author and
       * position — collaboration context humans want. The MODEL gets structure with the semantics
       * spelled out, because the flat string taught it exactly the wrong lesson: given
       * "(top-center) admin@demo.local: make this green" it read the email as the SUBJECT and the
       * position as a PLACEMENT, and painted admin@demo.local in green across the app's header.
       * Metadata that is not labeled as metadata becomes content.
       */
      const commentLines = filled.map((n, i) => `${i + 1}. (${describePos(n.x, n.y)}) ${n.comments.map(c => `${c.author}: ${c.text}`).join(' | ')}`);
      const modelLines = filled.map((n, i) => {
        const requests = n.comments.map(c => `"${c.text}"`).join(' and ');
        return `Pin ${i + 1} — sits at the ${describePos(n.x, n.y)} of the picture, marking the element to change. Requested change: ${requests}`;
      });
      /*
       * Per-pin close-ups are the targeting mechanism; the full frame is context. Grounding on
       * "top-center" alone once sent the model to the top bar of a DIFFERENT screen. Capped at 4
       * crops so the full snapshot always fits in the attachment budget.
       */
      const crops = await Promise.all(filled.slice(0, 4).map(n => cropAt(n.x, n.y)));
      const prompt =
        `We marked up a snapshot of my app. Each numbered pin on the first attached picture marks ONE element; apply that pin's requested change to the element it marks.\n` +
        `For each pin there is also a close-up image (pin-N-closeup) showing exactly the pinned element — identify the target from the CLOSE-UP first, and say which element you identified in your note. ` +
        `The snapshot shows one specific screen of the app: the change belongs to that element on that screen, not to a lookalike elsewhere.\n` +
        `A pin's position (like "top-center") only says where the pin sits — it is never an instruction to move, add, or place anything at that position. Only the quoted requested change is something to do; nothing else in a note is content to add to the app.` +
        (modelLines.length ? `\n\n${modelLines.join('\n')}` : '') +
        (drawings.length ? `\n\n(We also drew on the picture to point things out.)` : '');
      const attachments: Attachment[] = png ? [{ name: 'snapshot-notes.png', mimeType: 'image/png', dataUrl: png }] : [];
      crops.forEach((crop, i) => {
        if (crop) attachments.push({ name: `pin-${i + 1}-closeup.png`, mimeType: 'image/png', dataUrl: crop });
      });
      await onSend(prompt, attachments, commentLines);
      onClose();
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      const t = (e.target as HTMLElement)?.tagName;
      if (t === 'TEXTAREA' || t === 'INPUT') return; // let inputs keep their own undo
      const meta = e.ctrlKey || e.metaKey;
      const k = e.key.toLowerCase();
      if (meta && k === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if (meta && (k === 'y' || (k === 'z' && e.shiftKey))) {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, undo, redo]);

  const live = drawRef.current;
  const items = live ? [...drawings, live] : drawings;
  const hasContent = notes.some(n => n.comments.length > 0) || drawings.length > 0;
  const nothingYet = notes.length === 0 && drawings.length === 0;
  const cursor = tool === 'note' ? 'cursor-copy' : tool === 'eraser' ? 'cursor-cell' : 'cursor-crosshair';

  const tools: Array<{ key: Tool; label: string; icon: React.ReactNode }> = [
    { key: 'note', label: 'Add note', icon: <IconNote /> },
    { key: 'pen', label: 'Pen', icon: <IconPen /> },
    { key: 'eraser', label: 'Eraser', icon: <IconEraser /> },
    { key: 'circle', label: 'Circle', icon: <IconCircle /> },
    { key: 'rect', label: 'Rectangle', icon: <IconRect /> },
    { key: 'arrow', label: 'Arrow', icon: <IconArrow /> },
  ];

  const renderItem = (d: Item) => {
    if (d.kind === 'pen') {
      return (
        <polyline
          key={d.id}
          points={d.points.map(p => `${p.x * 100},${p.y * 100}`).join(' ')}
          fill="none"
          stroke={d.color}
          strokeWidth={d.size}
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      );
    }
    if (d.kind === 'rect') {
      const bb = bbox(d.a, d.b);
      return <rect key={d.id} x={bb.x * 100} y={bb.y * 100} width={bb.w * 100} height={bb.h * 100} fill="none" stroke={d.color} strokeWidth={d.size} vectorEffect="non-scaling-stroke" />;
    }
    if (d.kind === 'circle') {
      const bb = bbox(d.a, d.b);
      return (
        <ellipse
          key={d.id}
          cx={(bb.x + bb.w / 2) * 100}
          cy={(bb.y + bb.h / 2) * 100}
          rx={(bb.w / 2) * 100}
          ry={(bb.h / 2) * 100}
          fill="none"
          stroke={d.color}
          strokeWidth={d.size}
          vectorEffect="non-scaling-stroke"
        />
      );
    }
    const g = arrowBarbs(d.a, d.b, aspect);
    return (
      <g key={d.id}>
        <line x1={d.a.x * 100} y1={d.a.y * 100} x2={d.b.x * 100} y2={d.b.y * 100} stroke={d.color} strokeWidth={d.size} strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        <polyline
          points={`${g.p1.x * 100},${g.p1.y * 100} ${d.b.x * 100},${d.b.y * 100} ${g.p2.x * 100},${g.p2.y * 100}`}
          fill="none"
          stroke={d.color}
          strokeWidth={d.size}
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
      </g>
    );
  };

  return (
    <div className="absolute inset-0 z-40 flex flex-col bg-page/95 text-ink backdrop-blur-sm">
      {/* top bar */}
      <div data-ui className="flex shrink-0 flex-wrap items-center gap-1 border-b border-line bg-surface px-3 py-2">
        <button type="button" onClick={onClose} aria-label="Close" title="Close" className="grid h-8 w-8 place-items-center rounded-lg text-danger transition hover:bg-danger/10">
          <IconClose />
        </button>
        <span className="ml-0.5 mr-1 text-xs font-bold text-ink">Snapshot notes</span>
        <div className="mx-1 h-5 w-px bg-line" />
        <button type="button" onClick={undo} disabled={nothingYet} aria-label="Undo" title="Undo (Ctrl+Z)" className="grid h-8 w-8 place-items-center rounded-lg text-muted transition hover:bg-page hover:text-ink disabled:opacity-40">
          <IconUndo />
        </button>
        <button type="button" onClick={redo} disabled={redoStack.length === 0} aria-label="Redo" title="Redo (Ctrl+Shift+Z)" className="grid h-8 w-8 place-items-center rounded-lg text-muted transition hover:bg-page hover:text-ink disabled:opacity-40">
          <IconRedo />
        </button>
        <button type="button" onClick={clearAll} disabled={nothingYet} className="rounded-lg px-2 py-1 text-xs font-semibold text-muted transition hover:bg-page hover:text-ink disabled:opacity-40">
          Clear
        </button>

        {/* zoom */}
        <div className="mx-1 h-5 w-px bg-line" />
        <div className="flex items-center gap-0.5">
          <button type="button" onClick={() => zoomBy(-ZOOM_STEP)} disabled={zoom <= ZOOM_MIN} aria-label="Zoom out" title="Zoom out" className="grid h-8 w-8 place-items-center rounded-lg text-muted transition hover:bg-page hover:text-ink disabled:opacity-40">
            <IconZoomOut />
          </button>
          <button type="button" onClick={() => setZoom(1)} title="Reset zoom" className="min-w-[3rem] rounded-lg px-1 py-1 text-center text-xs font-semibold tabular-nums text-muted transition hover:bg-page hover:text-ink">
            {Math.round(zoom * 100)}%
          </button>
          <button type="button" onClick={() => zoomBy(ZOOM_STEP)} disabled={zoom >= ZOOM_MAX} aria-label="Zoom in" title="Zoom in" className="grid h-8 w-8 place-items-center rounded-lg text-muted transition hover:bg-page hover:text-ink disabled:opacity-40">
            <IconZoomIn />
          </button>
        </div>

        <div className="ml-auto">
          <button
            type="button"
            onClick={() => void send()}
            disabled={busy || sending || !hasContent}
            className="rounded-lg bg-accent px-3.5 py-1.5 text-xs font-bold text-accent-ink transition hover:opacity-90 disabled:opacity-40"
          >
            {sending ? 'Sending…' : 'Send notes'}
          </button>
        </div>
      </div>

      {/* canvas area with floating rails — padding reserves room so rails never cover the snapshot */}
      <div className="relative min-h-0 flex-1">
        <div className="absolute inset-0 flex items-center justify-center overflow-auto py-6 pl-32 pr-24">
          <div
            ref={surfaceRef}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            className={['relative max-h-full max-w-full select-none rounded-xl bg-white shadow-2xl ring-1 ring-black/5', cursor].join(' ')}
            style={{ touchAction: 'none', zoom }}
          >
            {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
            <img
              ref={imgRef}
              src={src}
              alt="Snapshot of your app"
              onLoad={e => {
                const im = e.currentTarget;
                if (im.naturalHeight) setAspect(im.naturalWidth / im.naturalHeight);
              }}
              className="block max-h-[calc(100vh-9rem)] max-w-full rounded-xl"
              draggable={false}
            />

            <svg className="pointer-events-none absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
              {items.map(renderItem)}
            </svg>

            {/* pins */}
            {notes.map((n, i) => (
              <div key={n.id} data-ui className="absolute" style={{ left: `${n.x * 100}%`, top: `${n.y * 100}%`, transform: 'translate(-50%,-50%)' }}>
                <button
                  type="button"
                  onPointerDown={e => onPinDown(e, n.id)}
                  onPointerMove={e => onPinMove(e, n.id)}
                  onPointerUp={e => onPinUp(e, n.id)}
                  className="grid h-6 w-6 cursor-grab touch-none place-items-center rounded-full border-2 border-white bg-accent text-[11px] font-bold text-accent-ink shadow-md transition hover:scale-110 active:cursor-grabbing"
                  aria-label={`Note ${i + 1}`}
                  title="Drag to move · click to open"
                >
                  {i + 1}
                </button>
                {n.open && (
                  <div className="absolute left-1/2 top-8 z-10 w-64 -translate-x-1/2 overflow-hidden rounded-2xl border border-line bg-surface shadow-xl">
                    <div className="flex items-center justify-between gap-2 border-b border-line px-3 py-2">
                      <span className="flex items-center gap-1.5 text-[11px] font-bold text-ink">
                        <span className="grid h-5 w-5 place-items-center rounded-full bg-accent text-[10px] text-accent-ink">{i + 1}</span>
                        Note
                      </span>
                      <div className="flex items-center gap-0.5">
                        <button type="button" onClick={() => deleteNote(n.id)} aria-label="Delete note" title="Delete note" className="grid h-6 w-6 place-items-center rounded-lg text-muted transition hover:bg-page hover:text-danger">
                          <IconTrash />
                        </button>
                        <button type="button" onClick={() => closeNote(n.id)} aria-label="Collapse note" title="Collapse" className="grid h-6 w-6 place-items-center rounded-lg text-muted transition hover:bg-page hover:text-ink">
                          <IconClose />
                        </button>
                      </div>
                    </div>

                    {n.comments.length > 0 && (
                      <ul className="max-h-44 space-y-2 overflow-auto px-3 py-2.5">
                        {n.comments.map(c => (
                          <li key={c.id} className="group flex gap-2">
                            <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-accent/15 text-[9px] font-bold text-accent-deep">{initials(c.author)}</span>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1">
                                <span className="truncate text-[10px] font-semibold text-muted">{c.author}</span>
                                <button
                                  type="button"
                                  onClick={() => removeComment(n.id, c.id)}
                                  aria-label="Remove comment"
                                  className="ml-auto shrink-0 text-[10px] text-muted opacity-0 transition group-hover:opacity-100 hover:text-danger"
                                >
                                  Remove
                                </button>
                              </div>
                              <p className="whitespace-pre-wrap break-words text-xs text-ink">{c.text}</p>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}

                    <div className="border-t border-line p-2">
                      <div className="flex items-end gap-1.5 rounded-xl border border-line bg-page px-2 py-1 transition focus-within:border-accent/60">
                        <textarea
                          autoFocus
                          rows={1}
                          value={drafts[n.id] || ''}
                          onChange={e => setDrafts(d => ({ ...d, [n.id]: e.target.value }))}
                          onKeyDown={e => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              addComment(n.id);
                            }
                          }}
                          placeholder={n.comments.length ? 'Reply…' : 'What should change here?'}
                          className="max-h-24 min-h-[1.5rem] flex-1 resize-none bg-transparent py-0.5 text-xs text-ink outline-none placeholder:text-muted"
                        />
                        <button
                          type="button"
                          onClick={() => addComment(n.id)}
                          disabled={!(drafts[n.id] || '').trim()}
                          aria-label="Add comment"
                          className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-accent text-accent-ink transition hover:opacity-90 disabled:opacity-40"
                        >
                          <IconSend />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* left tool rail */}
        <div data-ui className="absolute left-3 top-1/2 flex -translate-y-1/2 items-start gap-2">
          <div className="flex flex-col gap-1 rounded-2xl border border-line bg-surface p-1.5 shadow-lg">
            {tools.map(t => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTool(t.key)}
                aria-label={t.label}
                aria-pressed={tool === t.key}
                title={t.label}
                className={['grid h-10 w-10 place-items-center rounded-xl transition', tool === t.key ? 'bg-accent/15 text-accent-deep' : 'text-muted hover:bg-page hover:text-ink'].join(' ')}
              >
                {t.icon}
              </button>
            ))}
          </div>

          {/* vertical size range — for pen / shapes */}
          {showSizes && (
            <div className="flex flex-col items-center gap-1.5 rounded-2xl border border-line bg-surface px-2 py-2.5 shadow-lg">
              {SIZES.map(s => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSize(s)}
                  aria-label={`Size ${s}`}
                  className={['grid h-8 w-8 place-items-center rounded-lg transition', size === s ? 'bg-accent/15' : 'hover:bg-page'].join(' ')}
                >
                  <span className="rounded-full border border-black/25" style={{ width: Math.max(4, s + 2), height: Math.max(4, s + 2), background: color }} />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* right color rail */}
        <div data-ui className="absolute right-3 top-1/2 flex -translate-y-1/2 flex-col items-center gap-2 rounded-2xl border border-line bg-surface px-2 py-2.5 shadow-lg">
          {COLORS.map(col => (
            <button
              key={col}
              type="button"
              onClick={() => setColor(col)}
              aria-label={`Colour ${col}`}
              className={['h-7 w-7 rounded-full border transition', color === col ? 'border-ink ring-2 ring-offset-1 ring-ink/40' : 'border-black/10 hover:scale-110'].join(' ')}
              style={{ background: col }}
            />
          ))}
          <div className="my-0.5 h-px w-5 bg-line" />
          {/* custom color picker */}
          <label
            className="relative grid h-7 w-7 cursor-pointer place-items-center rounded-full border border-black/10"
            style={{ background: 'conic-gradient(from 0deg, #ef4444, #f59e0b, #22c55e, #06b6d4, #3b82f6, #8b5cf6, #ec4899, #ef4444)' }}
            title="Custom color"
          >
            <span className="h-3 w-3 rounded-full border border-white shadow-sm" style={{ background: COLORS.includes(color) ? '#ffffff' : color }} />
            <input type="color" value={color} onChange={e => setColor(e.target.value)} className="absolute inset-0 cursor-pointer opacity-0" aria-label="Pick a custom color" />
          </label>
        </div>
      </div>
    </div>
  );
}
